import { lazy, Suspense, useEffect, useMemo } from 'react'
import {
  Switch,
  Route,
  useParams,
  useHistory,
  useRouteMatch
} from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  getRootPlans as getRootPlansAction,
  clearPlans as clearPlansAction
} from '../../PlanSelection/actions'
import {
  getProjectDetails as getProjectDetailsAction,
  clearProjectDetails as clearProjectDetailsAction
} from '../../ProjectSettings/actions'
import {
  makeSelectProjectDetails,
  makeSelectGetProjectDetailsError
} from '../../ProjectSettings/selectors'
import { makeSelectGetPlanRevisionError } from '../../Plan/selectors'
import {
  Loader,
  NoBgLoaderWrapper
} from '../../../components/page-loader/styled-components'
import * as quickSelectActions from '../../QuickSelect/actions'
import { AsyncLoaderPage } from '../../../components/async-loader/async-loader-page'

const PlanSelection = lazy(() => import('../../PlanSelection'))
const PlanViewerNew = lazy(() => import('../../PlanViewerNew'))
const TeamManagement = lazy(() => import('../../TeamsManagement'))
const TaskList = lazy(() => import('../../TaskList'))
const Datasets = lazy(() => import('../../Datasets'))
const Media = lazy(() => import('../../Media'))
const Reports = lazy(() => import('../../Reports'))
const Groups = lazy(() => import('../../Groups'))
const Group = lazy(() => import('../../Group'))
const PlanVersions = lazy(() => import('../../PlanVersions'))
const NotFoundPage = lazy(() => import('../../NotFoundPage'))

const ProjectRouter = () => {
  const { projectId } = useParams()
  const history = useHistory()
  const { path } = useRouteMatch()
  const dispatch = useDispatch()

  // Seletors
  const projectDetails = useSelector(makeSelectProjectDetails())
  const projectDetailsError = useSelector(makeSelectGetProjectDetailsError())
  const planRevisionError = useSelector(makeSelectGetPlanRevisionError())

  // actions
  const getRootPlans = (...attributes) =>
    dispatch(getRootPlansAction(...attributes))
  const clearPlans = (...attributes) => {
    dispatch(clearPlansAction(...attributes))
    dispatch(quickSelectActions.clearPlans())
  }
  const getProjectDetails = (...attributes) =>
    dispatch(getProjectDetailsAction(...attributes))
  const clearProjectDetails = (...attributes) =>
    dispatch(clearProjectDetailsAction(...attributes))

  // UseEffects
  useEffect(() => {
    if (projectId) {
      clearPlans()
      getRootPlans(projectId, 0, 'Title', 'Active', '')
      if (!projectDetails || projectDetails.id !== projectId) {
        clearProjectDetails()
        getProjectDetails(projectId)
      }
    }
  }, [projectId, projectDetails])

  useEffect(() => {
    if (
      (!!projectDetailsError && projectDetailsError.status === 403) ||
      planRevisionError?.status === 403
    ) {
      history.push('/project', { data: 'permission denied' })
    }
  }, [projectDetailsError, planRevisionError])

  useEffect(() => {
    if (projectId && !['/project/:projectId/:move?'].includes(path)) {
      clearPlans()
      getRootPlans(projectId, 0, 'Title', 'Active', '')
    }
  }, [projectId])

  // consts
  const teamRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/team/importStatus',
        component: TeamManagement,
        exact: true
      },
      {
        path: '/project/:projectId/team/:viewType/importStatus',
        component: TeamManagement,
        exact: true
      },
      { path: '/project/:projectId/team/:viewType?', component: TeamManagement }
    ],
    []
  )

  const taskRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/tasks/importStatus',
        component: TaskList,
        exact: true
      },
      {
        path: '/project/:projectId/tasks/new',
        component: TaskList,
        exact: true
      },
      { path: '/project/:projectId/tasks', component: TaskList, exact: true }
    ],
    []
  )

  const datasetRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/datasets/importStatus',
        component: Datasets,
        exact: true
      },
      {
        path: '/project/:projectId/datasets/:datasetId/importStatus',
        component: Datasets,
        exact: true
      },
      {
        path: '/project/:projectId/datasets/:datasetId',
        component: Datasets,
        exact: true
      },
      { path: '/project/:projectId/datasets', component: Datasets, exact: true }
    ],
    []
  )

  const mediaRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/media/importStatus',
        component: Media,
        exact: true
      },
      { path: '/project/:projectId/media', component: Media }
    ],
    []
  )

  const reportRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/reports/importStatus',
        component: Reports,
        exact: true
      },
      { path: '/project/:projectId/reports', component: Reports }
    ],
    []
  )

  const groupRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/groups/importStatus',
        component: Groups,
        exact: true
      },
      {
        path: '/project/:projectId/groups/:groupId/:viewType/:planId?/:pinId?/:tab?/:pinOption?/:page?/:size?/:search?',
        component: Group
      },
      { path: '/project/:projectId/groups', component: Groups, exact: true }
    ],
    []
  )

  const planVersionRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/planVersions/:planId/importStatus',
        component: PlanVersions,
        exact: true
      },
      {
        path: '/project/:projectId/planVersions/:planId',
        component: PlanVersions,
        exact: true
      }
    ],
    []
  )

  const pinRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/pins/:viewType/noPlan/:pinId?/:tab?/:pinOption?/:page?/:size?/:search?',
        component: PlanViewerNew
      },
      {
        path: '/project/:projectId/pins/:viewType/:planId?/:pinId?/:tab?/:pinOption?/:page?/:size?/:search?',
        component: PlanViewerNew
      },
      {
        path: '/project/:projectId/pins/:viewType/:planId?/:pinId?/:tab?/:pinOption?/:search?',
        component: PlanViewerNew
      }
    ],
    []
  )

  const importRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/importRevision/:planId/:option?',
        component: PlanSelection,
        exact: true
      },
      {
        path: '/project/:projectId/importStatus',
        component: PlanSelection,
        exact: true
      },
      {
        path: '/project/:projectId/import',
        component: PlanSelection,
        exact: true
      }
    ],
    []
  )

  const commonProjectRoutes = useMemo(
    () => [
      {
        path: '/project/:projectId/move',
        component: PlanSelection,
        exact: true
      },
      {
        path: '/project/:projectId/settings',
        component: PlanSelection,
        exact: true
      },
      {
        path: '/project/:projectId/newfolder/:parentId?',
        component: PlanSelection,
        exact: true
      },
      {
        path: '/project/:projectId/rename/:planId',
        component: PlanSelection,
        exact: true
      },
      {
        path: '/project/:projectId/:planId/:viewType/:pinId?/:tab?/:pinOption?/:search?',
        component: PlanViewerNew
      },
      { path: '/project/:projectId', component: PlanSelection, exact: true }
    ],
    []
  )
  if (!projectDetails || projectDetails.id !== projectId) {
    return (
      <NoBgLoaderWrapper>
        <Loader className="loader" />
      </NoBgLoaderWrapper>
    )
  }

  return (
    <>
      <Suspense fallback={AsyncLoaderPage}>
        <Switch>
          {/* Todas as rotas em ordem de especificidade */}
          {teamRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {taskRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {datasetRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {mediaRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {reportRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {groupRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {planVersionRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {pinRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {importRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          {commonProjectRoutes.map((route) => (
            <Route key={route.path} {...route} />
          ))}

          <Route component={NotFoundPage} />
        </Switch>
      </Suspense>
    </>
  )
}

export default ProjectRouter
